/**
 * 공통으로 사용되는 타입 정의
 */

// ========================================
// 공통 Enum 타입
// ========================================

/**
 * 트랙 상태 타입
 */
export type TrackStatus = 
  | 'SCHEDULED'  // 예정됨
  | 'ONSALE'     // 판매 중
  | 'ENDED'      // 판매 종료
  | 'SOLDOUT'    // 품절
  | 'CANCELLED'  // 취소됨
  | 'ACTIVE'     // 활성
  | 'PURCHASED'  // 구매 완료
  | 'VERIFIED';  // 검증됨

/**
 * 아티스트 타입
 */
export type ArtistType = 'SOLO' | 'GROUP';

/**
 * 네트워크 타입
 */
export type NetworkType = 'ETHEREUM' | 'POLYGON' | 'KLAYTN' | 'SOLANA';

/**
 * 주문 상태
 */
export type OrderStatus = 
  | 'PENDING'   // 대기 중
  | 'PAID'      // 결제 완료
  | 'FAILED'    // 결제 실패
  | 'CANCELLED' // 취소됨
  | 'REFUNDED'; // 환불됨

/**
 * 결제 방법
 */
export type PaymentMethod = 
  | 'CARD'   // 신용카드
  | 'BANK'   // 계좌이체
  | 'VBANK'  // 가상계좌
  | 'PHONE'; // 휴대폰

/**
 * 결제 상태
 */
export type PaymentStatus = 
  | 'READY'     // 결제 대기
  | 'PAID'      // 결제 완료
  | 'CANCELLED' // 취소됨
  | 'FAILED';   // 실패

/**
 * 결제 제공자
 */
export type PaymentProvider = 'PORTONE';

/**
 * 인증 제공자
 */
export type AuthProvider = 'email' | 'google' | 'kakao';

/**
 * 약관 카테고리
 */
export type TermCategory = 'TERMS' | 'PRIVACY' | 'REFUND' | 'DATA';

/**
 * 토큰 표준
 */
export type TokenStandard = 'ERC-721' | 'ERC-1155';

/**
 * 지갑 상태
 */
export type WalletStatus = 'inPlatform' | 'transferred';

/**
 * 다운로드 포맷
 */
export type DownloadFormat = 'WAV' | 'FLAC' | 'MP3';

/**
 * 인증서 상태
 */
export type CertificateStatus = 'ACTIVE' | 'REVOKED' | 'EXPIRED';

/**
 * 인증 코드 타입
 */
export type VerificationCodeType = 'signup' | 'resetPassword';

// ========================================
// API 공통 응답 구조
// ========================================

/**
 * 표준 API 응답 래퍼
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * 메타데이터를 포함한 API 응답 (axios 인터셉터용)
 */
export interface ApiResponseWithMeta<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  _meta: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
  };
}

/**
 * 페이지네이션 데이터 구조
 */
export interface PaginatedData<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * 페이지네이션 응답 (표준 래퍼 포함)
 */
export interface PaginatedResponse<T> {
  success: boolean;
  data: PaginatedData<T>;
  message?: string;
}

// ========================================
// 유연한 API 응답 타입
// ========================================

/**
 * 배열 또는 페이지네이션 데이터를 담을 수 있는 유연한 응답 타입
 */
export type FlexibleApiResponse<T> = 
  | ApiResponse<T[]>                    // { success, data: [...] }
  | ApiResponse<PaginatedData<T>>;      // { success, data: { count, results: [...] } }

/**
 * 배열 또는 페이지네이션 데이터 유니온 타입
 */
export type FlexibleData<T> = T[] | PaginatedData<T>;

// ========================================
// 타입 가드 함수
// ========================================

/**
 * 페이지네이션 데이터 여부 확인
 */
export function isPaginatedData<T>(
  data: FlexibleData<T>
): data is PaginatedData<T> {
  return (
    data !== null &&
    typeof data === 'object' &&
    'count' in data &&
    'results' in data &&
    Array.isArray((data as PaginatedData<T>).results)
  );
}

/**
 * 배열 데이터 여부 확인
 */
export function isArrayData<T>(data: FlexibleData<T>): data is T[] {
  return Array.isArray(data);
}

/**
 * FlexibleData에서 배열 추출
 */
export function extractArray<T>(data: FlexibleData<T>): T[] {
  if (isPaginatedData(data)) {
    return data.results;
  }
  if (isArrayData(data)) {
    return data;
  }
  return [];
}

/**
 * FlexibleData에서 총 개수 추출
 */
export function extractCount<T>(data: FlexibleData<T>): number {
  if (isPaginatedData(data)) {
    return data.count;
  }
  if (isArrayData(data)) {
    return data.length;
  }
  return 0;
}

/**
 * FlexibleData에서 페이지네이션 정보 추출
 */
export function extractPagination<T>(data: FlexibleData<T>): {
  hasNext: boolean;
  hasPrevious: boolean;
  count: number;
} {
  if (isPaginatedData(data)) {
    return {
      hasNext: data.next !== null,
      hasPrevious: data.previous !== null,
      count: data.count,
    };
  }
  return {
    hasNext: false,
    hasPrevious: false,
    count: isArrayData(data) ? data.length : 0,
  };
}


/**
 * API 에러 응답
 */
export interface ApiError {
  status: number;
  message: string;
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
}

// ========================================
// 공통 도메인 인터페이스
// ========================================

/**
 * 사용자 기본 정보
 */
export interface UserResponse {
  uid: string;
  email: string;
  full_name: string;
  provider?: AuthProvider;
  created_at?: string;
  updated_at?: string;
}

/**
 * 날짜 범위
 */
export interface DateRange {
  start: string;
  end: string;
}

/**
 * 가격 정보
 */
export interface PriceInfo {
  amount: number;
  currency: string;
}

/**
 * 페이지네이션 요청 파라미터
 */
export interface PaginationParams {
  page?: number;
  page_size?: number;
}

/**
 * 정렬 파라미터
 */
export interface SortingParams {
  ordering?: string;
}

/**
 * 검색 파라미터
 */
export interface SearchParams {
  search?: string;
}

/**
 * 공통 쿼리 파라미터 (페이지네이션 + 정렬 + 검색)
 */
export interface CommonQueryParams extends PaginationParams, SortingParams, SearchParams {}